/**
 * Module: `formatting/clipboard.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import type {
  List,
  ListItem,
  Nodes,
  PhrasingContent,
  Root,
  RootContent,
  Table,
} from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";

import { buildResponseExportDocument } from "@/lib/export";
import { SOURCE_DOCUMENT_SEPARATOR_TEXT } from "@/lib/export/source-document-separator";
import { SOURCES_DOCUMENTAIRES_HEADING } from "@/lib/sources/source-categories";
import { capitalizeFirstLetter } from "@/lib/ui-helper";
import type { Message, SidoniSource, Source } from "@/types";

/**
 * Extract text from a DOM node recursively
 */
function extractTextFromMessageNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;

    // Skip SVG and other non-text elements
    if (
      element.tagName === "SVG" ||
      element.tagName === "PATH" ||
      element.tagName === "CIRCLE" ||
      element.tagName === "RECT" ||
      element.tagName === "G" ||
      element.classList.contains("lucide")
    ) {
      return "";
    }

    // Handle anchor tags - include URL inline with text
    if (element.tagName === "A") {
      const href = element.getAttribute("href");
      const linkText = Array.from(element.childNodes)
        .filter((child) => {
          if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as Element;
            return el.tagName !== "SVG" && !el.classList.contains("lucide");
          }
          return true;
        })
        .map((child) => extractTextFromMessageNode(child))
        .join("")
        .trim();

      // Only include external URLs
      if (href && (href.startsWith("http://") || href.startsWith("https://"))) {
        return linkText ? `${linkText} (${href})` : href;
      }
      return linkText;
    }

    // Process children
    const children = Array.from(node.childNodes);
    const childTexts = children
      .map((child) => extractTextFromMessageNode(child))
      .filter((t) => t.length > 0);

    const blockElements = [
      "DIV",
      "P",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
      "LI",
      "BR",
    ];
    if (blockElements.includes(element.tagName)) {
      return childTexts.join("\n");
    }
    return childTexts.join("");
  }

  return "";
}

/**
 * Format message content for clipboard with section-aware formatting.
 * Extracts text from the rendered DOM and includes section headers and source cards.
 * Kept as a fallback for non-exportable content.
 */
export function formatDomMessageForClipboard(
  contentRef: HTMLDivElement
): string {
  // Find all section headers
  const sections: Array<{
    element: Element;
    type: "h3" | "alert" | "accordion";
    text: string;
  }> = [];

  // Find h3 headers
  contentRef.querySelectorAll("h3").forEach((h3) => {
    const text = extractTextFromMessageNode(h3).trim();
    if (text) sections.push({ element: h3, type: "h3", text });
  });

  // Find Avertissement alert
  const alert = contentRef.querySelector(".alert");
  if (alert) {
    const title = alert.querySelector("strong, .alert-title");
    if (title && title.textContent?.trim() === "Avertissement") {
      sections.push({ element: alert, type: "alert", text: "Avertissement" });
    }
  }

  // Find Accordion triggers (category sections)
  contentRef
    .querySelectorAll("[role='button'], .accordion-trigger")
    .forEach((trigger) => {
      const categorySpan = trigger.querySelector("span.font-semibold");
      if (categorySpan) {
        const categoryText = extractTextFromMessageNode(categorySpan)
          .trim()
          .replace(/\s*\(\d+\)\s*$/, "");
        if (categoryText && categoryText !== "Sources documentaires") {
          sections.push({
            element: trigger,
            type: "accordion",
            text: categoryText,
          });
        }
      }
    });

  // Sort sections by DOM position
  sections.sort((a, b) => {
    const pos = a.element.compareDocumentPosition(b.element);
    return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  // Build text with headers and separators by traversing DOM in order
  let result = "";
  let previousWasSection = false;
  let previousWasSourceCard = false;

  // Create a tree walker to traverse elements in order
  const walker = document.createTreeWalker(
    contentRef,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        const el = node as Element;
        // Skip SVG and non-content elements
        if (
          el.tagName === "SVG" ||
          el.tagName === "PATH" ||
          el.tagName === "CIRCLE" ||
          el.tagName === "RECT" ||
          el.tagName === "G" ||
          el.classList.contains("lucide")
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );

  const processedElements = new Set<Element>();

  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const element = node as Element;

    // Check if this is a section header we've identified
    const section = sections.find((s) => s.element === element);
    if (section && !processedElements.has(element)) {
      const prefix = previousWasSection || previousWasSourceCard ? "\n\n" : "";
      result += `${prefix}--- [${section.text}] ---\n\n`;
      previousWasSection = true;
      previousWasSourceCard = false;
      processedElements.add(element);
      continue;
    }

    // Check if this is a source card
    const isSourceCard =
      element.tagName === "DIV" &&
      element.classList.contains("bg-card") &&
      element.classList.contains("border-b") &&
      element.querySelector("a[href^='http']");

    if (isSourceCard && !processedElements.has(element)) {
      if (previousWasSourceCard) {
        result += "\n---\n\n";
      }
      result += extractTextFromMessageNode(element).trim() + "\n";
      previousWasSourceCard = true;
      previousWasSection = false;
      processedElements.add(element);
      continue;
    }

    // For other content, extract text if not already processed
    if (
      !processedElements.has(element) &&
      !element.closest("div.bg-card.border-b") && // Don't double-process source cards
      !sections.some(
        (s) =>
          element === s.element ||
          element.closest(`h3, .alert, [role='button']`) === s.element
      )
    ) {
      const text = extractTextFromMessageNode(element).trim();
      if (text && text.length > 0) {
        // Only add if it's meaningful content (not just whitespace from structure)
        const hasSubElements = element.querySelector(
          "div, p, span, a, h1, h2, h3, h4, h5, h6"
        );
        if (
          !hasSubElements ||
          element.tagName === "P" ||
          element.tagName === "SPAN"
        ) {
          result += text + "\n";
        }
      }
    }
  }

  // If no sections found, just extract text normally
  if (sections.length === 0) {
    result = extractTextFromMessageNode(contentRef);
  }

  return result.replace(/\n{3,}/g, "\n\n").trim();
}

function formatClipboardSourceLine(source: {
  title: string;
  url?: string;
  links?: Array<{ label: string; url: string }>;
  datePublication?: string;
  dateMiseAJour?: string;
  date?: string;
  summary?: string;
  sourceType?: string;
  documentType?: string;
  classeDocument?: string;
  avertissementDocument?: string;
  articleRefs?: Array<{ nouveau: string; ancien?: string | null }>;
  piecesJointes?: Array<{ title: string; url: string }>;
  outilsLies?: Array<{ title: string; url: string }>;
}): string {
  const lines: string[] = [];

  let line = source.title;
  if (source.url) {
    line += ` (${source.url})`;
  }
  lines.push(line);

  const metaParts: string[] = [];
  const classTypeParts: string[] = [];
  if (source.classeDocument?.trim()) {
    classTypeParts.push(source.classeDocument.trim());
  }
  if (source.documentType?.trim()) {
    classTypeParts.push(source.documentType.trim());
  }
  if (classTypeParts.length > 0) {
    metaParts.push(classTypeParts.join(" | "));
  }
  if (source.datePublication) {
    metaParts.push(`Publication : ${source.datePublication}`);
  }
  if (source.dateMiseAJour || source.date) {
    metaParts.push(`Mise à jour : ${source.dateMiseAJour ?? source.date}`);
  }
  for (const part of metaParts) {
    lines.push(capitalizeFirstLetter(part));
  }

  if (source.summary) {
    lines.push(capitalizeFirstLetter(`Résumé : ${source.summary}`));
  }

  if (source.articleRefs?.length) {
    lines.push(capitalizeFirstLetter(`Référence(s) d'article(s) Légifrance :`));
    for (const ref of source.articleRefs) {
      const ancien = ref.ancien ? ` (anciennement ${ref.ancien})` : "";
      lines.push(capitalizeFirstLetter(`${ref.nouveau}${ancien}`));
    }
  }

  if (source.avertissementDocument) {
    lines.push(
      capitalizeFirstLetter(
        `Avertissement de lecture : ${source.avertissementDocument}`
      )
    );
  }

  for (const link of source.links ?? []) {
    if (!link.url.trim()) continue;
    lines.push(capitalizeFirstLetter(`${link.label} : ${link.url}`));
  }

  if (source.piecesJointes?.length) {
    lines.push(capitalizeFirstLetter(`Pièce(s) jointe(s) :`));
    for (const pj of source.piecesJointes) {
      lines.push(`- ${capitalizeFirstLetter(pj.title)} (${pj.url})`);
    }
  }

  if (source.outilsLies?.length) {
    lines.push(capitalizeFirstLetter(`Outil(s) lié(s) :`));
    for (const outil of source.outilsLies) {
      lines.push(`- ${capitalizeFirstLetter(outil.title)} (${outil.url})`);
    }
  }

  return lines.join("\n");
}

function phrasingToClipboardText(nodes: PhrasingContent[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case "text":
          return node.value;
        case "inlineCode":
          return `\`${node.value}\``;
        case "strong":
        case "emphasis":
        case "delete":
          return "children" in node
            ? phrasingToClipboardText(node.children)
            : "";
        case "link": {
          const text = phrasingToClipboardText(node.children).trim();
          return text ? `${text} (${node.url})` : node.url;
        }
        case "break":
          return "\n";
        default:
          return "children" in node && Array.isArray(node.children)
            ? phrasingToClipboardText(node.children as PhrasingContent[])
            : "value" in node && typeof node.value === "string"
              ? node.value
              : "";
      }
    })
    .join("");
}

function extractPlainNodeText(node: Nodes): string {
  if ("value" in node && typeof node.value === "string") {
    return node.value;
  }
  if ("children" in node && Array.isArray(node.children)) {
    return node.children
      .map((child) => extractPlainNodeText(child as Nodes))
      .join("");
  }
  return "";
}

function renderClipboardList(node: List, depth = 0): string[] {
  return node.children.flatMap((item, index) =>
    renderClipboardListItem(item, node.ordered ? index + 1 : null, depth)
  );
}

function renderClipboardListItem(
  item: ListItem,
  number: number | null,
  depth: number
): string[] {
  const prefix = number === null ? "- " : `${number}. `;
  const indent = "  ".repeat(depth);
  const lines: string[] = [];

  item.children.forEach((child, childIndex) => {
    if (child.type === "paragraph") {
      const text = phrasingToClipboardText(child.children).trim();
      if (!text) return;
      if (childIndex === 0) {
        lines.push(`${indent}${prefix}${text}`);
      } else {
        lines.push(`${indent}  ${text}`);
      }
      return;
    }

    if (child.type === "list") {
      lines.push(...renderClipboardList(child, depth + 1));
      return;
    }

    lines.push(...renderClipboardBlock(child, depth));
  });

  return lines;
}

function renderClipboardTable(node: Table): string[] {
  return node.children.map((row) =>
    row.children
      .map((cell) =>
        cell.children
          .map((child) =>
            child.type === "paragraph"
              ? phrasingToClipboardText(child.children).trim()
              : extractPlainNodeText(child as Nodes).trim()
          )
          .filter(Boolean)
          .join(" ")
      )
      .join(" | ")
  );
}

function renderClipboardBlock(node: RootContent, depth = 0): string[] {
  switch (node.type) {
    case "heading":
      return [phrasingToClipboardText(node.children).trim()];
    case "paragraph":
      return [phrasingToClipboardText(node.children).trim()];
    case "list":
      return renderClipboardList(node, depth);
    case "table":
      return renderClipboardTable(node);
    case "code":
      return node.value
        .split("\n")
        .map((line) => `${"  ".repeat(depth)}${line}`);
    case "blockquote":
      return node.children.flatMap((child) =>
        renderClipboardBlock(child, depth)
      );
    case "thematicBreak":
      return ["---"];
    default: {
      const text = extractPlainNodeText(node as Nodes).trim();
      return text ? [text] : [];
    }
  }
}

function renderClipboardMarkdown(markdown: string): string {
  const tree = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .parse(markdown) as Root;
  return tree.children
    .flatMap((child) => renderClipboardBlock(child))
    .filter(Boolean)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Clipboard serialization aligned with PDF/Word content:
 * title, export date, question, answer, then Sources documentaires by category.
 */
export function formatResponseExportForClipboard(
  assistantMessage: Message,
  questionText: string | null | undefined,
  enrichedSources?: Array<Source | SidoniSource>
): string {
  const doc = buildResponseExportDocument({
    assistantMessage,
    questionText,
    enrichedSources,
  });

  const parts: string[] = [];

  if (doc.disclaimer?.trim()) {
    parts.push(doc.disclaimer.trim(), "");
  }

  parts.push(doc.title, `Exporté le ${doc.exportedAt}`, "");

  if (doc.question.text.trim()) {
    parts.push("Question", doc.question.text.trim(), "");
  }

  parts.push(
    "Réponse",
    renderClipboardMarkdown(doc.answerMarkdown) || "(vide)",
    ""
  );

  if (doc.sourceCategories.length > 0) {
    parts.push(SOURCES_DOCUMENTAIRES_HEADING);
    for (const category of doc.sourceCategories) {
      parts.push(category.label);
      for (const [sourceIndex, source] of category.sources.entries()) {
        if (sourceIndex > 0) {
          parts.push(SOURCE_DOCUMENT_SEPARATOR_TEXT);
        }
        parts.push(formatClipboardSourceLine(source));
      }
      parts.push("");
    }
  }

  return parts
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Legacy entry point retained for current callers until all cases are migrated.
 */
export function formatMessageForClipboard(contentRef: HTMLDivElement): string {
  return formatDomMessageForClipboard(contentRef);
}
